import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Moon, Clock, Sparkles, Brain, Activity } from 'lucide-react';
import { WhoopDayData } from '@/hooks/useWhoopData';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { format } from 'date-fns';
import WhoopMetricCard from './WhoopMetricCard';
import { TimeRange } from './WhoopTimeRangeSelector';

interface WhoopSleepDetailProps {
  data: WhoopDayData[];
  selectedData: WhoopDayData | null;
  timeRange: TimeRange;
}

export default function WhoopSleepDetail({ data, selectedData, timeRange }: WhoopSleepDetailProps) {
  const getSleepColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 85) return 'text-accent';
    if (score >= 70) return 'text-primary';
    return 'text-secondary';
  };

  const getSleepStatus = (score?: number | null) => {
    if (!score) return 'No Data';
    if (score >= 85) return 'Excellent Sleep';
    if (score >= 70) return 'Good Sleep';
    return 'Needs Improvement';
  };

  const getSleepBgClass = (score?: number | null) => {
    if (!score) return 'bg-muted/20';
    if (score >= 85) return 'bg-accent/10 border-accent/30';
    if (score >= 70) return 'bg-primary/10 border-primary/30';
    return 'bg-secondary/10 border-secondary/30';
  };

  const formatDuration = (minutes: number | null | undefined) => {
    if (!minutes) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Calculate stats
  const validSleep = data.filter(d => d.sleep_performance !== null);
  const avgSleep = validSleep.length > 0 
    ? validSleep.reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
    : 0;
  
  const validDuration = data.filter(d => d.sleep_duration_minutes !== null);
  const avgDuration = validDuration.length > 0 
    ? validDuration.reduce((sum, d) => sum + (d.sleep_duration_minutes || 0), 0) / validDuration.length 
    : 0;

  // Calculate trend
  const midPoint = Math.floor(validSleep.length / 2);
  const recentAvg = midPoint > 0 
    ? validSleep.slice(0, midPoint).reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / midPoint 
    : avgSleep;
  const olderAvg = midPoint > 0 
    ? validSleep.slice(midPoint).reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / (validSleep.length - midPoint) 
    : avgSleep;
  const trendDiff = recentAvg - olderAvg;
  const trend: 'up' | 'down' | 'stable' = trendDiff > 3 ? 'up' : trendDiff < -3 ? 'down' : 'stable';

  // Prepare chart data
  const chartData = [...data].reverse().map(d => ({
    date: format(new Date(d.date), timeRange === 'month' ? 'MMM d' : 'EEE'),
    fullDate: format(new Date(d.date), 'EEE, MMM d'),
    performance: d.sleep_performance,
    duration: d.sleep_duration_minutes ? d.sleep_duration_minutes / 60 : null,
    quality: d.sleep_quality_score
  }));

  // Sleep performance distribution
  const distributionData = [
    { range: 'Poor (<70%)', count: validSleep.filter(d => (d.sleep_performance || 0) < 70).length, fill: 'hsl(var(--secondary))' },
    { range: 'Good (70-84%)', count: validSleep.filter(d => (d.sleep_performance || 0) >= 70 && (d.sleep_performance || 0) < 85).length, fill: 'hsl(var(--primary))' },
    { range: 'Excellent (85%+)', count: validSleep.filter(d => (d.sleep_performance || 0) >= 85).length, fill: 'hsl(var(--accent))' },
  ];

  // Calculate sleep debt
  const optimalHours = 8;
  const sleepDebt = validDuration.reduce((debt, d) => {
    const hours = (d.sleep_duration_minutes || 0) / 60;
    return debt + Math.max(0, optimalHours - hours);
  }, 0);

  // For period views, calculate summary values
  const periodLabel = timeRange === 'day' 
    ? (selectedData ? format(new Date(selectedData.date), 'EEEE, MMMM d') : 'Today')
    : timeRange === 'week' 
      ? '7-Day Average' 
      : '30-Day Average';

  const displaySleep = timeRange === 'day' 
    ? selectedData?.sleep_performance 
    : avgSleep;
  const displayDuration = timeRange === 'day' 
    ? selectedData?.sleep_duration_minutes 
    : avgDuration;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Main Summary - Shows single day or averages based on timeRange */}
      {(selectedData || data.length > 0) && (
        <Card className={`border-2 ${getSleepBgClass(displaySleep)}`}>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              {timeRange === 'day' ? 'Sleep Performance' : 'Average Sleep Performance'}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {periodLabel}
              {timeRange !== 'day' && ` • ${data.length} days of data`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            <div className="text-center py-2 sm:py-4">
              <div className={`text-5xl sm:text-7xl font-bold ${getSleepColor(displaySleep)}`}>
                {displaySleep ? Math.round(displaySleep) : '--'}%
              </div>
              <p className={`text-base sm:text-lg font-medium mt-1 sm:mt-2 ${getSleepColor(displaySleep)}`}>
                {getSleepStatus(displaySleep)}
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              <WhoopMetricCard
                icon={Clock}
                label={timeRange === 'day' ? 'Total Sleep' : 'Avg Sleep'}
                value={formatDuration(displayDuration)}
                colorClass="text-primary"
                bgClass="bg-primary/5"
              />
              <WhoopMetricCard
                icon={Sparkles}
                label={timeRange === 'day' ? 'Sleep Quality' : 'Avg Quality'}
                value={timeRange === 'day' 
                  ? (selectedData?.sleep_quality_score ? Math.round(selectedData.sleep_quality_score) : '--')
                  : (avgSleep ? Math.round(avgSleep) : '--')}
                unit="%"
                colorClass="text-accent"
                bgClass="bg-accent/5"
              />
              <WhoopMetricCard
                icon={Brain}
                label="Days Tracked"
                value={data.length}
                colorClass="text-primary"
                bgClass="bg-primary/5"
              />
              <WhoopMetricCard
                icon={Activity}
                label="Sleep Debt"
                value={sleepDebt.toFixed(1)}
                unit="hrs"
                colorClass={sleepDebt > 5 ? 'text-secondary' : 'text-accent'}
                bgClass="bg-muted/10"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Overview Cards - Only show for week/month with multiple days */}
      {timeRange !== 'day' && data.length > 1 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <WhoopMetricCard
            icon={Moon}
            label={`Avg Sleep (${timeRange === 'week' ? '7d' : '30d'})`}
            value={avgSleep.toFixed(0)}
            unit="%"
            trend={trend}
            trendValue={`${trendDiff > 0 ? '+' : ''}${trendDiff.toFixed(0)}%`}
            colorClass={getSleepColor(avgSleep)}
            bgClass={getSleepBgClass(avgSleep)}
          />
          <WhoopMetricCard
            icon={Clock}
            label="Avg Duration"
            value={formatDuration(avgDuration)}
            colorClass="text-primary"
            bgClass="bg-primary/5"
          />
          <WhoopMetricCard
            icon={Activity}
            label="Total Sleep Debt"
            value={sleepDebt.toFixed(1)}
            unit="hrs"
            colorClass={sleepDebt > 5 ? 'text-secondary' : 'text-accent'}
            bgClass="bg-secondary/5"
          />
          <WhoopMetricCard
            icon={Sparkles}
            label="Days Tracked"
            value={validSleep.length}
            colorClass="text-accent"
            bgClass="bg-accent/5"
          />
        </div>
      )}

      {/* Sleep Performance Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sleep Performance Trend</CardTitle>
          <CardDescription>Track your sleep quality over time</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [`${Math.round(value)}%`, 'Sleep Performance']}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <ReferenceLine y={avgSleep} stroke="hsl(var(--accent))" strokeDasharray="5 5" />
              <Area
                type="monotone"
                dataKey="performance"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#sleepGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Sleep Duration Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sleep Duration</CardTitle>
          <CardDescription>Hours of sleep per night (optimal: 7-9 hours)</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 12]} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [`${value?.toFixed(1)} hrs`, 'Duration']}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <ReferenceLine y={8} stroke="hsl(var(--accent))" strokeDasharray="5 5" label={{ value: '8hr goal', fill: 'hsl(var(--accent))', fontSize: 10 }} />
              <Bar 
                dataKey="duration" 
                fill="hsl(var(--secondary))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Sleep Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sleep Quality Distribution</CardTitle>
          <CardDescription>Breakdown of your sleep performance scores</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {distributionData.map((item) => (
              <div key={item.range} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{item.range}</span>
                  <span className="text-muted-foreground">
                    {item.count} nights ({validSleep.length > 0 ? Math.round((item.count / validSleep.length) * 100) : 0}%)
                  </span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: validSleep.length > 0 ? `${(item.count / validSleep.length) * 100}%` : '0%',
                      backgroundColor: item.fill
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sleep Insights */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Sleep & Mental Wellbeing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground/90 leading-relaxed">
            Quality sleep is essential for emotional regulation, memory consolidation, and stress resilience. Your sleep performance reflects how well your body restored overnight.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-accent/10 rounded-lg border border-accent/30">
              <p className="text-sm font-medium text-accent">Good Sleep (70%+)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Better emotional regulation, improved focus, enhanced stress resilience
              </p>
            </div>
            <div className="p-3 bg-secondary/10 rounded-lg border border-secondary/30">
              <p className="text-sm font-medium text-secondary">Poor Sleep (&lt;70%)</p>
              <p className="text-xs text-muted-foreground mt-1">
                May experience increased irritability, reduced cognitive function, heightened anxiety
              </p>
            </div>
          </div>
          {sleepDebt > 7 && (
            <div className="p-3 bg-secondary/20 rounded-lg border border-secondary/40">
              <p className="text-sm font-medium text-secondary">⚠️ Sleep Debt Alert</p>
              <p className="text-xs text-muted-foreground mt-1">
                You've accumulated {sleepDebt.toFixed(1)} hours of sleep debt. Consider prioritising rest over the next few days.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
